import React, { useState, useLayoutEffect, useRef, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import "./Supplement.scss";
import { useSelectedResult } from "../../contexts/SelectedResultContext";
import { useAppContext } from "../../contexts/AppContext";
import { getUserDetails } from "../../services/authService";
import { showSuccessToast } from "../../services/swalService";
import { FaSuitcase, FaSuitcaseRolling } from "react-icons/fa";
import { convertPrice, formatAgentWalletAmountIls } from "../../services/currencyService";
import {
  formatIlsAmountAsUsdString,
  formatPrimaryUsdSecondaryIls,
  renderUsdWithIlsHint,
} from "../../utils/vacationPackageCurrencyDisplay";
import { formatIlsPrimaryWithUsdParentheticalLine } from "../../utils/formatDualCurrencyDisplay";
import { isVacationPackagesFlightsHotelAgentDeal } from "../../utils/vacationPackageRoomAgentMarkup";
import { resolveVacationPackageIncludedBaggage } from "../../utils/resolveVacationPackageIncludedBaggage";
import {
  isLdsLuggageSupplementType,
  normalizeSupplementOurFareIlsAmount,
  shouldFormatSupplementOurFareAsAuthoritativeIls,
} from "../../utils/lds/formatLdsSupplementOurFareIls";
import { PASSENGER_KEYS } from "../PassengersData/PassengersData";

const LUGGAGE_TYPE_SET = new Set(["LUGGAGE", "BAGGAGE", "TROLLEY"]);

function supplementEntryIsLuggage(type, typeData) {
  if (typeData?.isGrouped) return true;
  const t = String(type || "").toUpperCase();
  if (LUGGAGE_TYPE_SET.has(t)) return true;
  const list = typeData?.supplements || [];
  return list.some((s) => LUGGAGE_TYPE_SET.has(String(s.type || s.Type || "").toUpperCase()));
}

const Supplement = ({
  disabled,
  isCustomerLink = false,
  showProviderSupplementFare = false,
  providerCurrency = "",
  /** 'all' | 'luggageOnly' | 'nonLuggageOnly' — פיצול תצוגה בזרימת חבילות (כבודה מול שאר תוספות) */
  supplementVisibilityMode = "all",
  /** זרימת package/view inline: דולר ראשי + (₪…) */
  b2cUsdPrimaryWithIlsHint = false,
  /** HotelsRiver DP luggage cards: ₪216 ($70) — display only */
  ilsPrimaryUsdParenthetical = false,
  /** public simple checkout: compact rows instead of poster cards */
  density = "default",
}) => {
  const context = useSelectedResult();
  const { isAppLoggedIn, USDrate, currencyRate, getBaggageFeeFlat } = useAppContext();
  
  // Early return if context is not available
  if (!context) {
    return null;
  }
  
  const { getSupplementsAsType, setLdsSupplement, ldsSupplement, ilsToUsd, ourSavedLDSOrder, agentData, selectedResult, initLdsSupplements, roomsAndPaxInfo } = context;
  
  // 🔧 FIX: Check if logged-in user is an agent (allows agents to edit supplements even on customer links)
  const loggedInUser = getUserDetails();
  const orderAgentId = ourSavedLDSOrder?.agent_id || 
                       ourSavedLDSOrder?.agent?.id || 
                       agentData?.id;
  const isLoggedInAgent = loggedInUser && loggedInUser !== false && loggedInUser.id && 
                         orderAgentId && 
                         loggedInUser.id.toString() === orderAgentId.toString();
  // Check if user is logged in as any agent (not necessarily the creator)
  const isAgentLoggedIn = loggedInUser && 
                         loggedInUser !== false && 
                         loggedInUser.id && 
                         isAppLoggedIn();
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const shouldRoundUpPriceForPackagesProfile =
    typeof pathname === "string" && pathname.includes("/packages/profile");

  const formatSupplementPriceIls = (amount, supForVpLuggagePricing = null) => {
    // ourFare from initLdsSupplements is already ILS — never convertPrice (double FX).
    // DP hotel profile often has hotel selectedResult (not DynamicPackage), so the old
    // isVacationPackagesFlightsHotelAgentDeal gate missed luggage and showed e.g. ₪665.
    const normalized = normalizeSupplementOurFareIlsAmount(amount, {
      roundUp: shouldRoundUpPriceForPackagesProfile,
    });
    if (normalized == null) return convertPrice(amount);
    if (
      shouldFormatSupplementOurFareAsAuthoritativeIls({
        type: supForVpLuggagePricing?.type,
        isCustomerLink,
        b2cUsdPrimaryWithIlsHint,
      })
    ) {
      if (ilsPrimaryUsdParenthetical) {
        const dualLine = formatIlsPrimaryWithUsdParentheticalLine(normalized, {
          ilsToUsd,
          currencyRate,
        });
        if (dualLine) return dualLine;
      }
      return formatAgentWalletAmountIls(normalized);
    }
    return convertPrice(normalized);
  };

  // Early return if required functions are not available
  if (!getSupplementsAsType) {
    return null;
  }

  const headers = [
    { text: "" },
    { text: t("packages.flight") },
    { text: t("packages.passenger") },
    { text: t("packages.baggage") },
    { text: t("packages.price") },
  ];

  const onClickSupplement = (supp, event) => {
    // 🔧 FIX: Remove all restrictions - allow customers to select/deselect supplements freely
    if (disabled || !setLdsSupplement) return;
    
    // Prevent default behavior to avoid scrolling
    if (event) {
      event.preventDefault();
      event.stopPropagation();
      // Immediately blur the button to prevent focus
      if (event.target) {
        event.target.blur();
      }
    }
    
    // Save current scroll position to prevent jumping
    const containers = [
      document.querySelector('.price-offer'),
      event?.target?.closest('.passangerData-supplements-wrapper')?.parentElement,
      event?.target?.closest('[class*="scroll"]'),
      document.documentElement
    ].filter(Boolean);
    
    const scrollContainer = containers[0] || document.documentElement;
    let scrollTop = 0;
    let scrollLeft = 0;
    
    if (scrollContainer === document.documentElement || scrollContainer === document.body) {
      scrollTop = window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0;
      scrollLeft = window.pageXOffset || document.documentElement.scrollLeft || document.body.scrollLeft || 0;
    } else {
      scrollTop = scrollContainer.scrollTop || 0;
      scrollLeft = scrollContainer.scrollLeft || 0;
    }
    
    // Save to ref for useLayoutEffect and mark that we're updating
    scrollPositionRef.current = { top: scrollTop, left: scrollLeft, container: scrollContainer };
    isUpdatingRef.current = true;
    
    const wasChecked = supp.checked || false;
    const seqNum = supp["@seqNum"];
    
    // 🔧 FIX: If supplement doesn't exist in state yet (came from selectedResult),
    // add it to state immediately with all required fields
      // Use the wrapper function from context which validates dealId
      setLdsSupplement((prevState) => {
      const currentSupp = prevState[seqNum];
      
      // If supplement doesn't exist in state, create it with all available fields
      if (!currentSupp) {
        console.log('[onClickSupplement] Supplement not in state, adding to state:', supp);
        const newSupp = {
          ...supp,
          checked: !wasChecked,
          // Ensure all required fields exist
          "@seqNum": seqNum,
          id: supp.id || seqNum,
          type: supp.type || 'LUGGAGE',
          description: supp.description || supp.desc || '',
          weightText: supp.weightText || '',
          flight: supp.flight || {
            fltRef: "-2",
            depCity: '',
            arrCity: '',
          },
          pax: supp.pax || {
            "@seqNum": supp.pax?.["@seqNum"] || 1,
            firstname: supp.pax?.firstname || '',
            lastname: supp.pax?.lastname || '',
          },
          ourFare: supp.ourFare || 0,
        };
        
        return {
          ...prevState,
          [seqNum]: newSupp,
        };
      }
      
      // If supplement exists, just toggle checked status
      console.log('[onClickSupplement] Toggling supplement checked status:', seqNum, 'from', wasChecked, 'to', !wasChecked);
      return {
        ...prevState,
        [seqNum]: {
          ...currentSupp,
          checked: !wasChecked,
        },
      };
    });
    
    // Also restore immediately in case useLayoutEffect doesn't catch it
    setTimeout(() => {
      if (scrollContainer === document.documentElement || scrollContainer === document.body) {
        window.scrollTo({
          top: scrollTop,
          left: scrollLeft,
          behavior: 'auto'
        });
      } else if (scrollContainer) {
        scrollContainer.scrollTop = scrollTop;
        scrollContainer.scrollLeft = scrollLeft;
      }
    }, 0);
    
    // Show encouragement message (for both customers and agents)
    if (!wasChecked) {
      const addMessages = [
        "מצוין! תוספת נוספה בהצלחה",
        "כל הכבוד! תוספת נבחרה",
        "מעולה! התוספת נוספה",
        "פנטסטי! תוספת נהדרת",
        "נהדר! התוספת נוספה בהצלחה",
        "מצוין! תוספת מעולה",
        "כל הכבוד! בחירה נהדרת",
        "מעולה! התוספת נוספה יפה",
        "פנטסטי! תוספת מושלמת",
        "נהדר! התוספת נוספה בהצלחה",
        "מצוין! תוספת נהדרת",
        "כל הכבוד! התוספת נוספה",
        "מעולה! תוספת מעולה",
        "פנטסטי! התוספת נוספה בהצלחה",
        "נהדר! תוספת נהדרת",
        "מצוין! התוספת נוספה יפה",
        "כל הכבוד! תוספת מושלמת",
        "מעולה! התוספת נוספה",
        "פנטסטי! תוספת נהדרת",
        "נהדר! התוספת נוספה בהצלחה מלאה"
      ];
      const randomMessage = addMessages[Math.floor(Math.random() * addMessages.length)];
      showSuccessToast(randomMessage);
    } else {
      const removeMessages = [
        "תוספת הוסרה בהצלחה",
        "כל הכבוד! תוספת הוסרה",
        "מעולה! התוספת הוסרה",
        "פנטסטי! תוספת הוסרה בהצלחה",
        "נהדר! התוספת הוסרה",
        "מצוין! תוספת הוסרה יפה",
        "כל הכבוד! התוספת הוסרה",
        "מעולה! תוספת הוסרה בהצלחה",
        "פנטסטי! התוספת הוסרה",
        "נהדר! תוספת הוסרה יפה מאוד",
        "מצוין! התוספת הוסרה",
        "כל הכבוד! תוספת הוסרה בהצלחה",
        "מעולה! התוספת הוסרה",
        "פנטסטי! תוספת הוסרה יפה",
        "נהדר! התוספת הוסרה בהצלחה",
        "מצוין! תוספת הוסרה",
        "כל הכבוד! התוספת הוסרה יפה",
        "מעולה! תוספת הוסרה בהצלחה",
        "פנטסטי! התוספת הוסרה",
        "נהדר! תוספת הוסרה בהצלחה מלאה"
      ];
      const randomMessage = removeMessages[Math.floor(Math.random() * removeMessages.length)];
      showSuccessToast(randomMessage);
    }
  };

  // 🔧 FIX: Load supplements from DB if ldsSupplement is empty but supplements exist in DB
  // Use ref to track the last order ID we processed to prevent infinite loop
  const lastProcessedOrderIdRef = useRef(null);
  
  // Track last dealId to detect package changes
  const lastDealIdRef = useRef(null);
  
  useEffect(() => {
    const currentOrderId = ourSavedLDSOrder?.id || ourSavedLDSOrder?.ourLdsId;
    const currentDealId = selectedResult?.dealId;
    const savedOrderDealId = ourSavedLDSOrder?.dealId || ourSavedLDSOrder?.dealid;
    
    // 🔧 FIX: If dealId changed, clear supplements immediately and reset tracking
    if (currentDealId && lastDealIdRef.current !== null && lastDealIdRef.current !== currentDealId) {
      console.log('[Supplement] Package changed - clearing supplements immediately', {
        oldDealId: lastDealIdRef.current,
        newDealId: currentDealId
      });
      setLdsSupplement({});
      lastProcessedOrderIdRef.current = null; // Reset to allow loading new supplements
      lastDealIdRef.current = currentDealId;
      return; // Don't load old supplements
    }
    
    // Update lastDealIdRef
    if (currentDealId) {
      lastDealIdRef.current = currentDealId;
    }
    
    // 🔧 FIX: Only load supplements if the dealId matches - prevent loading supplements from previous package
    if (currentDealId && savedOrderDealId && currentDealId !== savedOrderDealId) {
      console.log('[Supplement] Skipping supplement load - dealId mismatch', {
        currentDealId,
        savedOrderDealId
      });
      return;
    }
    
    // Skip if we've already processed this order
    if (currentOrderId && lastProcessedOrderIdRef.current === currentOrderId) {
      return;
    }
    
    // Check if ldsSupplement is empty (using current value, not in dependencies)
    const isEmpty = !ldsSupplement || Object.keys(ldsSupplement).length === 0;
    
    if (isEmpty && 
        ourSavedLDSOrder?.supplement && 
        typeof ourSavedLDSOrder.supplement === 'object' && 
        Object.keys(ourSavedLDSOrder.supplement).length > 0) {
      console.log('[Supplement] ldsSupplement is empty, but supplements exist in DB - loading them', {
        dealId: currentDealId,
        savedOrderDealId
      });
      // Try to parse and load supplements from DB
      let dbSupplements = ourSavedLDSOrder.supplement;
      if (typeof dbSupplements === 'string') {
        try {
          dbSupplements = JSON.parse(dbSupplements);
        } catch (e) {
          console.error('[Supplement] Error parsing DB supplements:', e);
          if (currentOrderId) {
            lastProcessedOrderIdRef.current = currentOrderId; // Mark as processed even on error
          }
          return;
        }
      }
      if (dbSupplements && typeof dbSupplements === 'object' && Object.keys(dbSupplements).length > 0) {
        // Load supplements from DB
        const supplementsFromDB = {};
        Object.keys(dbSupplements).forEach(key => {
          const supp = dbSupplements[key];
          supplementsFromDB[key] = {
            ...supp,
            checked: supp.checked === true || supp.checked === 'true'
          };
        });
        console.log('[Supplement] Loading supplements from DB:', supplementsFromDB);
        setLdsSupplement(supplementsFromDB);
        if (currentOrderId) {
          lastProcessedOrderIdRef.current = currentOrderId; // Mark as processed
        }
      }
    } else if (currentOrderId) {
      // If ldsSupplement already has data or no supplements in DB, mark as processed
      lastProcessedOrderIdRef.current = currentOrderId;
    }
  }, [ourSavedLDSOrder, setLdsSupplement, selectedResult?.dealId, ldsSupplement]); // Added ldsSupplement to detect when it's cleared

  // Use useState instead of useMemo to prevent infinite loops
  const [supplementsAsType, setSupplementsAsType] = useState({});
  
  // Use refs to track previous values and prevent unnecessary recalculations
  const prevLdsSupplementKeysRef = useRef('');
  const prevSelectedResultDealIdRef = useRef('');
  const prevRoomsCountRef = useRef(0);
  const prevLdsSupplementCheckedRef = useRef('');
  const isCalculatingRef = useRef(false);
  
  // Calculate stable comparison values
  const ldsSupplementKeys = ldsSupplement ? Object.keys(ldsSupplement).sort().join(',') : '';
  // 🔧 FIX: Also track checked status to detect when supplements are toggled
  const ldsSupplementChecked = ldsSupplement ? Object.values(ldsSupplement).map(s => `${s["@seqNum"]}:${s.checked || false}`).sort().join(',') : '';
  const selectedResultDealId = selectedResult?.dealId || '';
  const roomsCount = roomsAndPaxInfo?.length || 0;
  
  // Update supplementsAsType only when dependencies actually change
  useEffect(() => {
    // Prevent infinite loop - don't recalculate if already calculating
    if (isCalculatingRef.current) {
      return;
    }
    
    // Check if values actually changed
    const hasChanged = 
      prevLdsSupplementKeysRef.current !== ldsSupplementKeys ||
      prevLdsSupplementCheckedRef.current !== ldsSupplementChecked ||
      prevSelectedResultDealIdRef.current !== selectedResultDealId ||
      prevRoomsCountRef.current !== roomsCount;
    
    // If nothing changed, don't recalculate
    if (!hasChanged) {
      return;
    }
    
    if (!getSupplementsAsType) {
      setSupplementsAsType({});
      return;
    }
    
    // Mark as calculating to prevent recursive calls
    isCalculatingRef.current = true;
    
    // Calculate supplements
    try {
      const result = getSupplementsAsType() || {};
      
      // 🔧 FIX: Merge checked status from ldsSupplement state into result
      // This ensures that supplements that were added to state are reflected in supplementsAsType
      if (ldsSupplement && Object.keys(ldsSupplement).length > 0) {
        Object.keys(result).forEach(type => {
          if (Array.isArray(result[type])) {
            result[type] = result[type].map(supp => {
              // Try to find matching supplement in state by @seqNum
              const stateSupp = ldsSupplement[supp["@seqNum"]];
              if (stateSupp) {
                // Update with checked status and all other fields from state
                return {
                  ...supp,
                  ...stateSupp,
                  checked: stateSupp.checked || false,
                };
              }
              // Also try to find by matching other fields (type, pax, flight)
              const matchingSupp = Object.values(ldsSupplement).find(s => 
                s.type === supp.type &&
                s.pax?.["@seqNum"] === supp.pax?.["@seqNum"] &&
                s.flight?.fltRef === supp.flight?.fltRef
              );
              if (matchingSupp) {
                return {
                  ...supp,
                  ...matchingSupp,
                  checked: matchingSupp.checked || false,
                };
              }
              return supp;
            });
          }
        });
      }
      
      setSupplementsAsType(result);
      
      // Update refs
      prevLdsSupplementKeysRef.current = ldsSupplementKeys;
      prevLdsSupplementCheckedRef.current = ldsSupplementChecked;
      prevSelectedResultDealIdRef.current = selectedResultDealId;
      prevRoomsCountRef.current = roomsCount;
    } catch (error) {
      console.error("Error getting supplements:", error);
      setSupplementsAsType({});
    } finally {
      // Reset calculating flag after a short delay to allow state to settle
      setTimeout(() => {
        isCalculatingRef.current = false;
      }, 0);
    }
  }, [getSupplementsAsType, ldsSupplementKeys, selectedResultDealId, roomsCount, isCustomerLink, ldsSupplement]);

  // Keep track of scroll position when supplements change
  const scrollPositionRef = useRef({ top: 0, left: 0, container: null });
  const isUpdatingRef = useRef(false);
  
  // Restore scroll position after render - only when we're updating
  useLayoutEffect(() => {
    if (!isUpdatingRef.current) return;
    
    const restoreScroll = () => {
      const { top, left, container } = scrollPositionRef.current;
      if (container === document.documentElement || container === document.body || !container) {
        window.scrollTo({
          top: top,
          left: left,
          behavior: 'auto'
        });
      } else if (container) {
        container.scrollTop = top;
        container.scrollLeft = left;
      }
      isUpdatingRef.current = false;
    };
    
    // Use multiple timeouts to ensure it works after all renders
    const timeoutId1 = setTimeout(() => {
      restoreScroll();
      const timeoutId2 = setTimeout(restoreScroll, 0);
      return () => clearTimeout(timeoutId2);
    }, 0);
    
    return () => clearTimeout(timeoutId1);
  }, [ldsSupplement]);

  // Determine icon based on supplement type/description
  const getSupplementIcon = (supp) => {
    const desc = (supp.description || "").toLowerCase();
    if (desc.includes("טרולי") || desc.includes("trolley") || desc.includes("carry")) {
      return <FaSuitcaseRolling className="supplement-icon" />;
    }
    return <FaSuitcase className="supplement-icon" />;
  };

  // Translate supplement description to Hebrew
  const translateDescription = (description) => {
    if (!description) return "";
    
    let translated = description;
    
    // Remove LDS first
    translated = translated.replace(/\bLDS\b/gi, "").trim();
    
    // Translate BAGGAGE
    translated = translated.replace(/BAGGAGE/gi, "מזוודה");
    translated = translated.replace(/baggage/gi, "מזוודה");

    // כיוון טיסה מהספק — לא בשורת המוצר (מוצג בשדה הכיוון)
    translated = translated.replace(/\bDEPARTURE\b/gi, "");
    translated = translated.replace(/\bRETURN\b/gi, "");
    translated = translated.replace(/\bOUTBOUND\b/gi, "");
    translated = translated.replace(/\bINBOUND\b/gi, "");
    
    // Translate TROLLEY
    translated = translated.replace(/TROLLEY/gi, "כבודת יד");
    translated = translated.replace(/trolley/gi, "כבודת יד");
    
    // Handle Hebrew "טרולי" - change to "כבודת יד"
    if (translated.includes("טרולי")) {
      translated = translated.replace(/תיק טרולי/g, "כבודת יד");
      translated = translated.replace(/טרולי/g, "כבודת יד");
    }
    
    // Remove 1PCS or 1 PCS (single unit)
    translated = translated.replace(/\b1\s*PCS\b/gi, "");
    translated = translated.replace(/\b1\s*יחידה\b/g, "");
    
    // Translate 2PCS, 3PCS, etc. (multiple units)
    translated = translated.replace(/(\d+)PCS/gi, (match, num) => {
      const count = parseInt(num);
      return count > 1 ? `${count} יחידות` : "";
    });
    translated = translated.replace(/(\d+)\s*PCS/gi, (match, num) => {
      const count = parseInt(num);
      return count > 1 ? `${count} יחידות` : "";
    });
    
    // Clean up multiple spaces
    translated = translated.replace(/\s+/g, " ").trim();
    
    return translated;
  };

  // Translate weight text to Hebrew
  const translateWeightText = (weightText) => {
    if (!weightText) return "";
    
    let translated = weightText;
    
    // Translate common weight units
    translated = translated.replace(/KG/gi, "ק\"ג");
    translated = translated.replace(/kg/gi, "ק\"ג");
    translated = translated.replace(/KGS/gi, "ק\"ג");
    translated = translated.replace(/kgs/gi, "ק\"ג");
    
    return translated;
  };

  console.log('[Supplement] Rendering component:', {
    supplementsAsTypeKeys: Object.keys(supplementsAsType).length,
    supplementsAsType,
    isCustomerLink,
    disabled
  });

  // Helper function to find which room a passenger belongs to
  const findRoomForPassenger = (passengerSeqNum) => {
    if (!roomsAndPaxInfo || !passengerSeqNum) return null;
    
    for (let roomIndex = 0; roomIndex < roomsAndPaxInfo.length; roomIndex++) {
      const room = roomsAndPaxInfo[roomIndex];
      const allPassengers = [
        ...(room.adults || []),
        ...(room.kids || []),
        ...(room.infants || [])
      ];
      
      const passenger = allPassengers.find(
        (pax) => (pax["@seqNum"] || pax.seqNum) === passengerSeqNum
      );
      
      if (passenger) {
        return { roomIndex, room, passenger };
      }
    }
    
    return null;
  };

  // Helper function to get passenger name
  const getPassengerName = (pax) => {
    if (!pax) return "נוסע";
    const firstname = pax.firstname || pax[PASSENGER_KEYS?.FIRSTNAME] || "";
    const lastname = pax.lastname || pax[PASSENGER_KEYS?.LASTNAME] || "";
    if (firstname || lastname) {
      return `${firstname} ${lastname}`.trim();
    }
    // Placeholders before names are filled on profile / checkout
    return "נוסע";
  };

  // Group supplements by room and passenger
  const groupSupplementsByRoomAndPassenger = (supplementsAsType) => {
    const grouped = {};
    
    Object.entries(supplementsAsType).forEach(([type, suppList]) => {
      // Only group luggage supplements (LUGGAGE, BAGGAGE, TROLLEY)
      const isLuggageType = type === "LUGGAGE" || type === "BAGGAGE" || type === "TROLLEY" || 
                            suppList.some(s => {
                              const suppType = s.type || s.Type || '';
                              return suppType === "LUGGAGE" || suppType === "BAGGAGE" || suppType === "TROLLEY";
                            });
      
      if (!isLuggageType) {
        // For non-luggage supplements, keep them in the original structure
        if (!grouped[type]) {
          grouped[type] = { isGrouped: false, supplements: suppList };
        }
        return;
      }
      
      // Group luggage supplements by room and passenger
      suppList.forEach((supp) => {
        const passengerSeqNum = supp.pax?.["@seqNum"] || supp.pax?.seqNum;
        const roomInfo = findRoomForPassenger(passengerSeqNum);
        
        if (!roomInfo) {
          console.warn('[Supplement] Passenger not found for supplement:', {
            passengerSeqNum,
            supplementSeqNum: supp["@seqNum"],
            supplementType: supp.type,
            roomsCount: roomsAndPaxInfo.length,
            allPassengerSeqNums: roomsAndPaxInfo.map((room, idx) => ({
              roomIndex: idx,
              adults: room.adults?.map(p => p["@seqNum"] || p.seqNum) || [],
              kids: room.kids?.map(p => p["@seqNum"] || p.seqNum) || [],
              infants: room.infants?.map(p => p["@seqNum"] || p.seqNum) || []
            }))
          });
        }
        
        if (roomInfo) {
          const { roomIndex, room, passenger } = roomInfo;
          const roomKey = `room_${roomIndex}`;
          const passengerKey = `pax_${passengerSeqNum}`;
          
          if (!grouped[type]) {
            grouped[type] = { isGrouped: true, rooms: {} };
          }
          
          if (!grouped[type].rooms[roomKey]) {
            grouped[type].rooms[roomKey] = {
              roomIndex,
              roomSeqNum: room["@seqNum"] || roomIndex + 1,
              passengers: {}
            };
          }
          
          if (!grouped[type].rooms[roomKey].passengers[passengerKey]) {
            grouped[type].rooms[roomKey].passengers[passengerKey] = {
              passengerSeqNum,
              passenger,
              passengerName: getPassengerName(passenger),
              supplements: []
            };
          }
          
          grouped[type].rooms[roomKey].passengers[passengerKey].supplements.push(supp);
        } else {
          // If passenger not found in any room, skip this supplement
          // This can happen if supplement was created before rooms were added
          // or if there's a mismatch in passenger seqNum
          // Don't add to ungrouped - skip it
        }
      });
    });
    
    return grouped;
  };

  // Check if supplements exist in selectedResult but not yet initialized
  const hasSupplementsInSelectedResult = selectedResult?.supplement && 
    (Array.isArray(selectedResult.supplement) ? selectedResult.supplement.length > 0 : 
     typeof selectedResult.supplement === 'object' && Object.keys(selectedResult.supplement).length > 0);
  const hasRoomsWithPassengers = roomsAndPaxInfo && roomsAndPaxInfo.length > 0 && 
    roomsAndPaxInfo.some(
      (room) => (room.adults && room.adults.length > 0) || (room.kids && room.kids.length > 0)
    );
  const needsPassengers = hasSupplementsInSelectedResult && !hasRoomsWithPassengers;

  // Group supplements by room and passenger
  const groupedSupplements = groupSupplementsByRoomAndPassenger(supplementsAsType);

  const vpFlightIncludedVm = useMemo(
    () =>
      resolveVacationPackageIncludedBaggage({
        linkedFlights: selectedResult?.linkedFlights,
        fullDeal: selectedResult,
      }),
    [selectedResult?.linkedFlights, selectedResult?.dealId, selectedResult?.ID]
  );

  const renderLuggageSupplementDualCurrencyPrice = (supp) => {
    const normalized = normalizeSupplementOurFareIlsAmount(supp.ourFare, {
      roundUp: shouldRoundUpPriceForPackagesProfile,
    });
    const dualLine = formatIlsPrimaryWithUsdParentheticalLine(
      normalized ?? supp.ourFare,
      { ilsToUsd, currencyRate },
    );
    if (dualLine) return dualLine;
    if (normalized != null) return formatAgentWalletAmountIls(normalized);
    return convertPrice(supp.ourFare);
  };

  const isCompactDensity = density === "compact";

  const shouldShowVpLuggageDualCurrencyOnCard = (supp) =>
    isLdsLuggageSupplementType(supp.type) &&
    (isCustomerLink ||
      ilsPrimaryUsdParenthetical ||
      isVacationPackagesFlightsHotelAgentDeal(selectedResult));

  /** תווית כיוון בעברית; ב־PERFLIGHTANDPAX: הלוך = רגל 0, חזור = רגל 1 (סדר תצוגה ב־RTL: הלוך מימין) */
  const getFlightInfoLabel = (supp) => {
    const fltRefStr =
      supp.flight?.fltRef != null ? String(supp.flight.fltRef) : "";
    const isPerFlightAndPax =
      supp?.orig?.supp?.category === "PERFLIGHTANDPAX" ||
      fltRefStr === "-3";
    if (isPerFlightAndPax) {
      const idx = Number(supp.flight?.flightIndex);
      if (idx === 0) return "טיסת הלוך";
      if (idx === 1) return "טיסת חזור";
      if (Number.isFinite(idx) && idx > 1) return `כיוון ${idx + 1}`;
      return "לנוסע לכיוון";
    }
    if (fltRefStr === "-2") return "טיסה הלוך וחזור";
    const dep = supp.flight?.depCity ?? "";
    const arr = supp.flight?.arrCity ?? "";
    if (dep || arr) return `${dep} → ${arr}`;
    return "";
  };

  // Render supplement button
  const renderSupplementButton = (supp) => {
    const isSelected = supp.checked;
    const isButtonDisabled = disabled;
    const flightInfo = getFlightInfoLabel(supp);
    const priceNode = !isButtonDisabled ? (
      <div
        className={`supplement-button-price${
          isCustomerLink && b2cUsdPrimaryWithIlsHint && !showProviderSupplementFare
            ? " supplement-button-price--dual-currency"
            : ""
        }`}
      >
        {showProviderSupplementFare
          ? (() => {
              const raw = Number(supp?.orig?.supp?.fare);
              if (!Number.isFinite(raw)) {
                if (shouldShowVpLuggageDualCurrencyOnCard(supp)) {
                  return renderLuggageSupplementDualCurrencyPrice(supp);
                }
                return isCustomerLink
                  ? b2cUsdPrimaryWithIlsHint
                    ? formatPrimaryUsdSecondaryIls(supp.ourFare, USDrate)
                    : formatIlsAmountAsUsdString(supp.ourFare, USDrate)
                  : formatSupplementPriceIls(supp.ourFare, supp);
              }
              const cur = providerCurrency
                ? String(providerCurrency).trim()
                : "";
              return cur ? `${raw} ${cur}` : String(raw);
            })()
          : shouldShowVpLuggageDualCurrencyOnCard(supp)
            ? renderLuggageSupplementDualCurrencyPrice(supp)
            : isCustomerLink && b2cUsdPrimaryWithIlsHint
              ? (() => {
                  const { primaryUsd, secondaryIls } = renderUsdWithIlsHint(
                    supp.ourFare,
                    USDrate
                  );
                  return (
                    <>
                      {primaryUsd ? (
                        <span className="supplement-price-usd">{primaryUsd}</span>
                      ) : null}
                      {secondaryIls ? (
                        <span className="supplement-price-ils">{secondaryIls}</span>
                      ) : null}
                    </>
                  );
                })()
              : isCustomerLink
                ? formatIlsAmountAsUsdString(supp.ourFare, USDrate)
                : formatSupplementPriceIls(supp.ourFare, supp)}
      </div>
    ) : null;

    return (
      <button
        key={supp["@seqNum"]}
        className={`supplement-button ${isSelected ? "supplement-button-selected" : "supplement-button-unselected"}${isCompactDensity ? " supplement-button--compact" : ""}`}
        onClick={(e) => !isButtonDisabled && onClickSupplement(supp, e)}
        onMouseDown={(e) => {
          // Prevent focus to avoid scrolling
          if (!isButtonDisabled) {
            e.preventDefault();
            // Immediately blur to prevent any focus
            setTimeout(() => {
              if (e.target) {
                e.target.blur();
              }
              if (document.activeElement === e.target) {
                document.activeElement.blur();
              }
            }, 0);
          }
        }}
        onFocus={(e) => {
          // Prevent scroll on focus - immediately blur
          if (!isButtonDisabled) {
            e.preventDefault();
            e.stopPropagation();
            e.target.blur();
          }
        }}
        tabIndex={-1}
        disabled={isButtonDisabled}
        type="button"
      >
        <div className="supplement-button-icon-wrapper">
          {getSupplementIcon(supp)}
        </div>
        <div className="supplement-button-content">
          <div className="supplement-button-title">
            {translateDescription(supp.description)} {translateWeightText(supp.weightText)}
          </div>
          <div className="supplement-button-details">
            {isCompactDensity ? null : (
              <div className="supplement-button-passenger">
                {getPassengerName(supp.pax)}
              </div>
            )}
            {flightInfo ? (
              <div className="supplement-button-flight">
                {flightInfo}
              </div>
            ) : null}
          </div>
          {isCompactDensity ? null : priceNode}
        </div>
        {isCompactDensity ? priceNode : null}
        {isCompactDensity ? (
          <span
            className={`supplement-button-checkmark${isSelected ? " is-on" : ""}`}
            aria-hidden
          >
            {isSelected ? "✓" : ""}
          </span>
        ) : isSelected ? (
          <div className="supplement-button-checkmark">✓</div>
        ) : null}
      </button>
    );
  };

  return (
    <div className={`tw-w-full supplement-container${isCompactDensity ? " supplement-container--compact" : ""}`}>
      {Object.entries(groupedSupplements).length === 0 ? (
        <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
          {needsPassengers ? (
            <div>
              <p style={{ marginBottom: '10px' }}>תוספות זמינות, אך יש לבחור חדרים ולמלא פרטי נוסעים קודם</p>
              <p style={{ fontSize: '14px', color: '#999' }}>לאחר מילוי פרטי הנוסעים, התוספות יופיעו כאן</p>
            </div>
          ) : (
            'אין תוספות זמינות'
          )}
        </div>
      ) : (
        Object.entries(groupedSupplements)
          .filter(([type, typeData]) => {
            if (!supplementVisibilityMode || supplementVisibilityMode === "all") return true;
            const isLug = supplementEntryIsLuggage(type, typeData);
            if (supplementVisibilityMode === "luggageOnly") return isLug;
            if (supplementVisibilityMode === "nonLuggageOnly") return !isLug;
            return true;
          })
          .map(([type, typeData], typeIndex) => {
          // If not grouped (non-luggage supplements), render as before
          if (!typeData.isGrouped) {
            return (
              <div key={typeIndex} className="supplement-type-section">
                <h3 className="supplement-type-title">{t(type)}</h3>
                <div className="supplement-buttons-grid">
                  {typeData.supplements
                    .filter((supp) => true)
                    .map((supp) => renderSupplementButton(supp))}
                </div>
              </div>
            );
          }

          // For grouped luggage supplements, render by room and passenger
          const rooms = typeData.rooms || {};
          
          // Find rooms that don't have supplements
          const roomsWithoutSupplements = roomsAndPaxInfo
            ?.map((room, roomIdx) => {
              const roomKey = `room_${roomIdx}`;
              const hasSupplements = rooms[roomKey] && Object.keys(rooms[roomKey].passengers || {}).length > 0;
              return { roomIndex: roomIdx, room, hasSupplements };
            })
            .filter(item => !item.hasSupplements && (item.room.adults?.length > 0 || item.room.kids?.length > 0)) || [];

          return (
            <div key={typeIndex} className="supplement-type-section">
              <h3 className="supplement-type-title">
                {isVacationPackagesFlightsHotelAgentDeal(selectedResult) &&
                !isCustomerLink &&
                vpFlightIncludedVm.hasAnyIncluded
                  ? `${t(type)} · כבודה נוספת`
                  : t(type)}
              </h3>

              {/* Show button to add supplements for rooms without supplements */}
              {roomsWithoutSupplements.length > 0 && (
                <div
                  className="supplement-add-room-section"
                  style={{
                    marginBottom: "20px",
                    padding: "15px",
                    backgroundColor: "#f5f5f5",
                    borderRadius: "8px",
                    textAlign: "center",
                  }}
                >
                  <p style={{ marginBottom: "10px", color: "#666" }}>
                    {vpFlightIncludedVm.hasAnyIncluded
                      ? roomsWithoutSupplements.length === 1
                        ? `חדר ${roomsWithoutSupplements[0].roomIndex + 1}: ניתן לרכוש כבודה נוספת (מעבר לכלולה בכרטיס)`
                        : `${roomsWithoutSupplements.length} חדרים — ניתן לרכוש כבודה נוספת (מעבר לכלולה בכרטיס)`
                      : roomsWithoutSupplements.length === 1
                        ? `חדר ${roomsWithoutSupplements[0].roomIndex + 1} עדיין לא כולל מזוודות וטרולי`
                        : `${roomsWithoutSupplements.length} חדרים עדיין לא כוללים מזוודות וטרולי`}
                  </p>
                  <button
                    type="button"
                    onClick={() => {
                      if (initLdsSupplements && selectedResult) {
                        // Re-initialize supplements to include all rooms
                        // Pass roomsAndPaxInfo explicitly to ensure we use the latest value
                        const currentRooms = roomsAndPaxInfo || [];
                        initLdsSupplements(selectedResult, undefined, currentRooms);
                      }
                    }}
                    disabled={disabled}
                    style={{
                      padding: '10px 20px',
                      backgroundColor: '#004E89',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: disabled ? 'not-allowed' : 'pointer',
                      fontSize: '14px',
                      fontWeight: '500',
                      opacity: disabled ? 0.6 : 1
                    }}
                    onMouseEnter={(e) => {
                      if (!disabled) {
                        e.target.style.backgroundColor = '#003366';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!disabled) {
                        e.target.style.backgroundColor = '#004E89';
                      }
                    }}
                  >
                    הוסף מזוודות וטרולי לחדרים
                  </button>
                </div>
              )}
              
              {/* Render by rooms */}
              {Object.keys(rooms).length > 0 && (
                <div className="supplement-rooms-container">
                  {Object.values(rooms)
                    .sort((a, b) => a.roomIndex - b.roomIndex)
                    .map((roomData) => (
                      <div key={`room_${roomData.roomIndex}`} className="supplement-room-section">
                        <h4 className="supplement-room-title">
                          חדר {roomData.roomSeqNum}
                        </h4>
                        
                        {/* Render by passengers */}
                        {Object.values(roomData.passengers)
                          .sort((a, b) => a.passengerSeqNum - b.passengerSeqNum)
                          .map((passengerData) => (
                            <div key={`pax_${passengerData.passengerSeqNum}`} className="supplement-passenger-section">
                              <h5 className="supplement-passenger-title">
                                {passengerData.passengerName || `נוסע ${passengerData.passengerSeqNum}`}
                              </h5>
                              <div className="supplement-buttons-grid supplement-buttons-grid--per-direction">
                                {passengerData.supplements
                                  .slice()
                                  .sort(
                                    (a, b) =>
                                      (Number(a.flight?.flightIndex) || 0) -
                                      (Number(b.flight?.flightIndex) || 0)
                                  )
                                  .map((supp) => renderSupplementButton(supp))}
                              </div>
                            </div>
                          ))}
                      </div>
                    ))}
                </div>
              )}
            </div>
          );
        })
      )}
    </div>
  );
};

export default Supplement;
